"use client";

import { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Drawer,
  Divider,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Support as SupportIcon,
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  Memory as MemoryIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import GeneralOverviewTab from "./vista-general";
import TechnicalSpecificationsTab from "./especificaciones-tecnicas";
import ImageGalleryTab from "./imagenes";
import TechnicalSupportTab from "./soporte-tecnico";
import NavBar from "../../../../../Navbar";

// ─── Constants ────────────────────────────────────────────────────────────────

const SIDEBAR_WIDTH = 240;

const NAV_ITEMS = [
  { id: 0, label: "Vista General", Icon: AssignmentIcon, Component: GeneralOverviewTab },
  { id: 1, label: "Especificaciones", Icon: BuildIcon, Component: TechnicalSpecificationsTab },
  { id: 2, label: "Imágenes", Icon: PhotoLibraryIcon, Component: ImageGalleryTab },
  { id: 3, label: "Soporte Técnico", Icon: SupportIcon, Component: TechnicalSupportTab },
];

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function SidebarContent({ equipo, activeSection, onSelect, onBack }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#0f172a", color: "#e2e8f0" }}>
      {/* Back + equipment info */}
      <Box sx={{ px: 2, pt: 2.5, pb: 1 }}>
        <Box
          onClick={onBack}
          sx={{
            display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
            color: "#64748b", "&:hover": { color: "#94a3b8" }, transition: "color 0.15s", mb: 2,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 15 }} />
          <Typography sx={{ fontSize: 12 }}>Volver a equipos</Typography>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mb: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <MemoryIcon sx={{ fontSize: 18, color: "#60a5fa" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {equipo?.equipo ?? "Equipo"}
            </Typography>
            {equipo?.sector && (
              <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.2 }}>
                {equipo.sector}
              </Typography>
            )}
          </Box>
        </Box>

        {equipo?.critico === 1 && (
          <Chip label="Crítico" size="small" sx={{ mt: 1, height: 18, fontSize: 10, fontWeight: 600, bgcolor: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }} variant="outlined" />
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mx: 2, mb: 1 }} />

      {/* Nav items */}
      <Box sx={{ flex: 1, px: 1.5, py: 1 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#475569", px: 1, mb: 1, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          Secciones
        </Typography>
        {NAV_ITEMS.map(({ id, label, Icon }) => {
          const isActive = activeSection === id;
          return (
            <Box
              key={id}
              onClick={() => onSelect(id)}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 1.5, py: 1, borderRadius: 1.5, cursor: "pointer", mb: 0.5,
                bgcolor: isActive ? "rgba(59,130,246,0.15)" : "transparent",
                borderLeft: isActive ? "3px solid #3b82f6" : "3px solid transparent",
                color: isActive ? "#93c5fd" : "#94a3b8",
                "&:hover": { bgcolor: isActive ? "rgba(59,130,246,0.18)" : "rgba(255,255,255,0.04)", color: isActive ? "#93c5fd" : "#cbd5e1" },
                transition: "all 0.15s",
              }}
            >
              <Icon sx={{ fontSize: 17, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, fontWeight: isActive ? 600 : 400 }}>{label}</Typography>
            </Box>
          );
        })}
      </Box>

      {equipo?.id && (
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 10, color: "#334155" }}>
            ID #{equipo.id}{equipo.zona ? ` · ${equipo.zona}` : ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Container ────────────────────────────────────────────────────────────────

export default function EquipmentDocumentationContainer() {
  const navigate = useNavigate();
  const location = useLocation();
  const equipo = location.state?.equipo;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeSection, setActiveSection] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleBack = () => navigate(-1);
  const handleSelect = (id) => { setActiveSection(id); setMobileOpen(false); };

  const ActiveComponent = NAV_ITEMS[activeSection].Component;

  const sidebarContent = (
    <SidebarContent equipo={equipo} activeSection={activeSection} onSelect={handleSelect} onBack={handleBack} />
  );

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <NavBar />

      <Box sx={{ display: "flex" }}>
        {/* Desktop sidebar — sticky, scrolls its own content */}
        {!isMobile && (
          <Box sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, height: "calc(100vh - 64px)", position: "sticky", top: 64, overflowY: "auto" }}>
            {sidebarContent}
          </Box>
        )}

        {/* Mobile drawer */}
        {isMobile && (
          <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}>
            {sidebarContent}
          </Drawer>
        )}

        {/* Main content — page scrolls naturally */}
        <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, minHeight: "calc(100vh - 64px)" }}>
          {/* Mobile hamburger */}
          {isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton size="small" onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: 15 }}>
                {equipo?.equipo ?? "Documentación"}
              </Typography>
            </Box>
          )}

          {/* Section header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            {!isMobile && (
              <Tooltip title="Volver a equipos">
                <IconButton size="small" onClick={handleBack} sx={{ bgcolor: "white", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f1f5f9" } }}>
                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: 17, color: "#0f172a", lineHeight: 1.2 }}>
                {NAV_ITEMS[activeSection].label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {equipo?.equipo}{equipo?.sector ? ` · ${equipo.sector}` : ""}
              </Typography>
            </Box>
          </Box>

          {/* Section content */}
          <Box
            key={activeSection}
            sx={{
              animation: "sectionIn 0.18s ease-out",
              "@keyframes sectionIn": {
                from: { opacity: 0, transform: "translateY(5px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <ActiveComponent />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
